import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { apiClient } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  Star, MapPin, Clock, Phone, CheckCircle, ArrowLeft,
  Calendar, User, Mail, MessageCircle, ChevronDown, Filter, X
} from 'lucide-react';

export default function PriestServicesPage() {
  const [searchParams] = useSearchParams();
  const [vendors, setVendors] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCity, setSelectedCity] = useState(searchParams.get('city') || '');
  const [selectedService, setSelectedService] = useState(searchParams.get('service') || '');
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [matchResult, setMatchResult] = useState(null);

  const [formData, setFormData] = useState({
    user_name: '',
    user_phone: '',
    user_email: '',
    service_sub_category: searchParams.get('service') || '',
    city: searchParams.get('city') || '',
    state: '',
    preferred_date: '',
    preferred_time: '',
    additional_details: '',
  });

  const cities = ['Hyderabad', 'Bangalore', 'Chennai'];
  const times = ['morning', 'afternoon', 'evening'];

  useEffect(() => {
    const load = async () => {
      try {
        const [catRes, venRes] = await Promise.all([
          apiClient.get('/services/catalog?category=priest'),
          apiClient.get(`/services/vendors?category=priest${selectedCity ? `&city=${selectedCity}` : ''}`),
        ]);
        setCatalog(catRes.data.catalog || []);
        setVendors(venRes.data.vendors || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedCity]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.user_name || !formData.user_phone || !formData.service_sub_category || !formData.city) {
      toast.error('Please fill in all required fields');
      return;
    }
    setFormLoading(true);
    try {
      const res = await apiClient.post('/services/request', {
        service_category: 'priest',
        ...formData,
      });
      setMatchResult(res.data);
      setSubmitted(true);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    } finally {
      setFormLoading(false);
    }
  };

  const filteredVendors = vendors.filter(v => {
    if (selectedService) {
      return v.services?.some(s => s.sub_category === selectedService && s.is_active);
    }
    return true;
  });

  if (submitted && matchResult) {
    return (
      <div className="min-h-screen bg-gray-50 py-12" data-testid="request-success">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Request Submitted!</h2>
            <p className="text-gray-500 mb-6">{matchResult.message}</p>
            {matchResult.matched_count > 0 && (
              <div className="bg-purple-50 rounded-xl p-4 mb-6 text-left">
                <p className="font-medium text-purple-900 mb-2">
                  {matchResult.matched_count} vendor(s) matched
                </p>
                <p className="text-sm text-purple-700">
                  They have been notified and will respond shortly. You will receive an email when a vendor accepts.
                </p>
              </div>
            )}
            <div className="flex gap-3 justify-center">
              <Button onClick={() => { setSubmitted(false); setShowRequestForm(false); }} variant="outline" data-testid="back-to-browse-btn">
                Browse More Services
              </Button>
              <Link to="/services">
                <Button data-testid="go-to-services-btn">All Services</Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="priest-services-page">
      {/* Header */}
      <section className="bg-gradient-to-br from-violet-900 via-purple-900 to-indigo-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-4">
          <Link to="/services" className="inline-flex items-center gap-1 text-purple-300 hover:text-white text-sm mb-4 transition-colors" data-testid="back-to-services-link">
            <ArrowLeft className="w-4 h-4" /> All Services
          </Link>
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">Priest & Puja Services</h1>
          <p className="text-purple-200">Book verified priests for Griha Pravesh, Vastu Shanti, and more</p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-8" data-testid="service-filters">
          <select
            value={selectedCity}
            onChange={e => setSelectedCity(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            data-testid="city-filter"
          >
            <option value="">All Cities</option>
            {cities.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            value={selectedService}
            onChange={e => setSelectedService(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            data-testid="service-type-filter"
          >
            <option value="">All Services</option>
            {catalog.map(c => <option key={c.sub_category} value={c.sub_category}>{c.display_name}</option>)}
          </select>
          <Button
            onClick={() => setShowRequestForm(true)}
            className="ml-auto bg-purple-600 hover:bg-purple-700"
            data-testid="request-service-btn"
          >
            Request a Service
          </Button>
        </div>

        {/* Request Form Modal */}
        {showRequestForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="request-form-overlay">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">Request Priest Service</h2>
                <button onClick={() => setShowRequestForm(false)} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="close-form-btn">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Service Type *</Label>
                  <select
                    value={formData.service_sub_category}
                    onChange={e => setFormData(p => ({...p, service_sub_category: e.target.value}))}
                    className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                    data-testid="form-service-type"
                  >
                    <option value="">Select a service</option>
                    {catalog.map(c => <option key={c.sub_category} value={c.sub_category}>{c.display_name}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Your Name *</Label>
                    <Input value={formData.user_name} onChange={e => setFormData(p => ({...p, user_name: e.target.value}))} required data-testid="form-name" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Phone *</Label>
                    <Input value={formData.user_phone} onChange={e => setFormData(p => ({...p, user_phone: e.target.value}))} required data-testid="form-phone" />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Email</Label>
                  <Input type="email" value={formData.user_email} onChange={e => setFormData(p => ({...p, user_email: e.target.value}))} data-testid="form-email" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">City *</Label>
                    <select
                      value={formData.city}
                      onChange={e => setFormData(p => ({...p, city: e.target.value}))}
                      className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      required
                      data-testid="form-city"
                    >
                      <option value="">Select city</option>
                      {cities.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Preferred Date</Label>
                    <Input type="date" value={formData.preferred_date} onChange={e => setFormData(p => ({...p, preferred_date: e.target.value}))} data-testid="form-date" />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Preferred Time</Label>
                  <select
                    value={formData.preferred_time}
                    onChange={e => setFormData(p => ({...p, preferred_time: e.target.value}))}
                    className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    data-testid="form-time"
                  >
                    <option value="">Any time</option>
                    {times.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-sm font-medium">Additional Details</Label>
                  <textarea
                    value={formData.additional_details}
                    onChange={e => setFormData(p => ({...p, additional_details: e.target.value}))}
                    rows={3}
                    className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                    placeholder="E.g., 3BHK flat, 20 guests expected..."
                    data-testid="form-details"
                  />
                </div>
                <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700" disabled={formLoading} data-testid="submit-request-btn">
                  {formLoading ? 'Submitting...' : 'Submit Request'}
                </Button>
              </form>
            </div>
          </div>
        )}

        {/* Vendor Cards */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3].map(i => <div key={i} className="h-64 bg-gray-200 rounded-xl animate-pulse" />)}
          </div>
        ) : filteredVendors.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
            <p className="text-gray-500 mb-4">No vendors found for the selected filters.</p>
            <Button variant="outline" onClick={() => { setSelectedCity(''); setSelectedService(''); }} data-testid="clear-filters-btn">
              Clear Filters
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="vendor-grid">
            {filteredVendors.map(vendor => (
              <div key={vendor.partner_id} className="bg-white rounded-xl border border-gray-100 hover:shadow-lg transition-all overflow-hidden" data-testid={`vendor-card-${vendor.partner_id}`}>
                <div className="p-6">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                      {vendor.name?.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{vendor.name}</h3>
                      <p className="text-sm text-gray-500">{vendor.company_name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {vendor.verification?.status === 'verified' && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                            <CheckCircle className="w-3 h-3" /> Verified
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                    <span className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                      {vendor.stats?.avg_rating || 'New'}
                    </span>
                    <span className="flex items-center gap-1">
                      <MessageCircle className="w-4 h-4" />
                      {vendor.stats?.total_reviews || 0} reviews
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {vendor.location?.city}
                    </span>
                  </div>

                  {/* Services */}
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {vendor.services?.filter(s => s.is_active).slice(0, 3).map(s => (
                      <span key={s.service_id} className="text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded-md">
                        {s.name}
                      </span>
                    ))}
                  </div>

                  {/* Price range */}
                  <div className="text-sm text-gray-600 mb-4">
                    Starting from{' '}
                    <span className="font-semibold text-gray-900">
                      Rs. {Math.min(...(vendor.services?.filter(s => s.is_active).map(s => s.price || 0) || [0])).toLocaleString()}
                    </span>
                  </div>

                  <Link to={`/vendor/${vendor.partner_id}`}>
                    <Button variant="outline" className="w-full" data-testid={`view-vendor-${vendor.partner_id}`}>
                      View Profile
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
